import { useEffect, useState, type FormEvent } from "react";
import type { AdminCompanyPatch, AdminCompanyRow, InviteLink } from "@omts/shared";
import { adminApi, fieldErrorsOf } from "../../api";
import { Modal } from "../../components/Modal";
import { fmtDateTime } from "../../time";
import { ActiveBadge, ConfirmActions, Field, InviteResult, TelegramTag, errMsg, useList } from "./common";

type FormState = { name: string; bin: string; fullName: string; phone: string };
type Errors = Partial<Record<keyof FormState | "form" | "contact", string>>;

const EMPTY: FormState = { name: "", bin: "", fullName: "", phone: "" };
const fromRow = (r: AdminCompanyRow): FormState => ({
  name: r.company.name,
  bin: r.company.bin ?? "",
  fullName: r.contact?.fullName ?? "",
  phone: r.contact?.phone ?? "",
});

/** Проверки до запроса — те же, что в zod на сервере, но с подписями по полям. */
function validate(f: FormState): Errors {
  const e: Errors = {};
  if (!f.name.trim()) e.name = "Обязательное поле";
  if (f.bin.trim() && !/^\d{12}$/.test(f.bin.trim())) e.bin = "БИН — 12 цифр";
  if (!f.fullName.trim()) e.fullName = "Обязательное поле";
  if (f.phone.trim().length < 5) e.phone = "Укажите телефон";
  return e;
}

/** Форма добавления/правки компании с единственным менеджером. */
function CompanyModal({ row, onClose, onDone }: { row: AdminCompanyRow | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<FormState>(() => (row ? fromRow(row) : EMPTY));
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const set = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = validate(form);
    if (Object.keys(v).length) return setErrors(v);
    const bin = form.bin.trim() || null;
    const contact = { fullName: form.fullName.trim(), phone: form.phone.trim() };
    setBusy(true);
    try {
      if (row) {
        const patch: AdminCompanyPatch = { name: form.name.trim(), bin, contact };
        await adminApi.updateCompany(row.company.id, patch);
      } else {
        await adminApi.createCompany({ name: form.name.trim(), bin, contact });
      }
      onDone();
    } catch (err) {
      setErrors({ ...fieldErrorsOf(err), form: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={row ? `Компания ${row.company.name}` : "Новая компания"} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        <Field label="Название компании" required error={errors.name}>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
        </Field>
        <Field label="БИН" error={errors.bin} hint="12 цифр, можно оставить пустым">
          <input value={form.bin} onChange={(e) => set("bin", e.target.value)} inputMode="numeric" maxLength={12} />
        </Field>
        <Field label="Менеджер (ФИО)" required error={errors.fullName}>
          <input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
        </Field>
        <Field label="Телефон менеджера" required error={errors.phone ?? errors.contact}>
          <input value={form.phone} onChange={(e) => set("phone", e.target.value)} type="tel" placeholder="+7 7xx xxx xx xx" />
        </Field>
        {row && !row.contact && <p className="hint warn">У компании нет контакта — заведён не через админку. Поля менеджера сохранить не получится.</p>}
        {errors.form && <p className="error">{errors.form}</p>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Сохранение…" : row ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({ row, onClose }: { row: AdminCompanyRow; onClose: () => void }) {
  const [invite, setInvite] = useState<InviteLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contactId = row.contact?.id;
  useEffect(() => {
    if (!contactId) return;
    adminApi.contactInvite(contactId).then(setInvite).catch((e: unknown) => setError(errMsg(e)));
  }, [contactId]);
  return (
    <Modal title={`Приглашение в Telegram — ${row.company.name}`} onClose={onClose}>
      <p className="muted">
        Менеджер: {row.contact?.fullName ?? "—"} · {row.contact?.phone ?? "—"}
      </p>
      {error ? <p className="error">{error}</p> : invite ? <InviteResult invite={invite} hint="Отправьте её менеджеру: он откроет бота и нажмёт «Start»." /> : <p className="muted">Создаём ссылку…</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </Modal>
  );
}

type Dialog = { kind: "add" } | { kind: "edit"; row: AdminCompanyRow } | { kind: "invite"; row: AdminCompanyRow } | { kind: "unlink"; row: AdminCompanyRow } | { kind: "deactivate"; row: AdminCompanyRow } | null;

export function CompaniesTab() {
  const { items, error, reload } = useList(adminApi.companies);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const close = () => {
    setDialog(null);
    setActionError(null);
  };
  const done = () => {
    close();
    reload();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      done();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const activate = (row: AdminCompanyRow) => {
    setRowError(null);
    adminApi
      .updateCompany(row.company.id, { isActive: true })
      .then(reload)
      .catch((e: unknown) => setRowError(errMsg(e)));
  };

  const rows = (items ?? []).filter((r) => showInactive || r.company.isActive);
  const inactiveCount = (items ?? []).filter((r) => !r.company.isActive).length;

  return (
    <>
      <div className="toolbar">
        <div className="row">
          <button className="btn primary" onClick={() => setDialog({ kind: "add" })}>
            Добавить компанию
          </button>
          {inactiveCount > 0 && (
            <label className="row hint" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              показывать неактивные ({inactiveCount})
            </label>
          )}
        </div>
        <span className="muted">{items ? `${items.length} компаний` : ""}</span>
      </div>
      {error && <p className="error">{error}</p>}
      {rowError && <p className="error">{rowError}</p>}
      <section className="card">
        {!items ? (
          <p className="muted">Загрузка…</p>
        ) : rows.length === 0 ? (
          <p className="muted">Компаний пока нет. Добавьте первую — менеджеру придёт ссылка на бота.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Компания</th>
                <th>БИН</th>
                <th>Менеджер</th>
                <th>Телефон</th>
                <th>Telegram</th>
                <th>Привязан</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const linked = r.contact?.telegramChatId != null;
                return (
                  <tr key={r.company.id} className={r.company.isActive ? "" : "inactive"}>
                    <td>
                      <b>{r.company.name}</b>
                      {!r.company.isActive && (
                        <>
                          {" "}
                          <ActiveBadge active={false} />
                        </>
                      )}
                    </td>
                    <td className="mono">{r.company.bin ?? "—"}</td>
                    <td>{r.contact?.fullName ?? <span className="muted">нет контакта</span>}</td>
                    <td className="nowrap">{r.contact?.phone ?? "—"}</td>
                    <td>{r.contact ? <TelegramTag linked={linked} username={r.contact.telegramUsername} /> : "—"}</td>
                    <td className="nowrap muted">{linked ? fmtDateTime(r.contact?.linkedAt) : "—"}</td>
                    <td>
                      <div className="actions-cell">
                        {r.contact && !linked && (
                          <button className="btn small" onClick={() => setDialog({ kind: "invite", row: r })}>
                            Ссылка-приглашение
                          </button>
                        )}
                        {r.contact && linked && (
                          <button className="btn small danger" onClick={() => setDialog({ kind: "unlink", row: r })}>
                            Отвязать Telegram
                          </button>
                        )}
                        <button className="btn small" onClick={() => setDialog({ kind: "edit", row: r })}>
                          Изменить
                        </button>
                        {r.company.isActive ? (
                          <button className="btn small" onClick={() => setDialog({ kind: "deactivate", row: r })}>
                            Деактивировать
                          </button>
                        ) : (
                          <button className="btn small" onClick={() => activate(r)}>
                            Активировать
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      <p className="hint">
        Неактивная компания не попадает в список получателей новых заявок; история её ставок сохраняется. Инвайт-ссылка — одноразовая:
        после смены менеджера отвяжите Telegram и выпустите новую.
      </p>

      {dialog?.kind === "add" && <CompanyModal row={null} onClose={close} onDone={done} />}
      {dialog?.kind === "edit" && <CompanyModal row={dialog.row} onClose={close} onDone={done} />}
      {dialog?.kind === "invite" && <InviteModal row={dialog.row} onClose={close} />}
      {dialog?.kind === "unlink" && (
        <Modal title="Отвязать Telegram?" onClose={close}>
          <p>
            {dialog.row.contact?.fullName} ({dialog.row.company.name}) перестанет получать заявки и не сможет делать ставки, пока не
            подключится заново по новой ссылке.
          </p>
          <ConfirmActions
            busy={busy}
            error={actionError}
            confirmLabel="Отвязать"
            danger
            onCancel={close}
            onConfirm={() => void run(() => adminApi.contactUnlink(dialog.row.contact!.id))}
          />
        </Modal>
      )}
      {dialog?.kind === "deactivate" && (
        <Modal title="Деактивировать компанию?" onClose={close}>
          <p>{dialog.row.company.name} исчезнет из списка получателей новых заявок. Её можно будет активировать снова.</p>
          <ConfirmActions
            busy={busy}
            error={actionError}
            confirmLabel="Деактивировать"
            danger
            onCancel={close}
            onConfirm={() => void run(() => adminApi.updateCompany(dialog.row.company.id, { isActive: false }))}
          />
        </Modal>
      )}
    </>
  );
}
